import {
  createAsyncThunk,
  createSelector,
  createSlice,
  SerializedError,
} from "@reduxjs/toolkit";

import { SearchService } from "services";
import {
  IAPIState,
  IStoreState,
  IErrorPayload,
  ISearchResults,
  ISearchResult,
  ISearchData,
} from "types";

export interface ISearchState {
  data?: ISearchResult[];
  meta: {
    query?: string;
    status: IAPIState;
    error?: SerializedError | null;
    pagination?: {
      page?: number;
      amount?: number;
      total?: number;
    };
    hasSearched?: boolean;
  };
}

const initialState: ISearchState = {
  data: [],
  meta: {
    query: "",
    status: IAPIState.idle,
    error: null,
    pagination: undefined,
    hasSearched: false,
  },
};

const NB_SEARCH_RESULT_ITEMS = 12;

export const doSearch = createAsyncThunk<
  // Return type of the payload creator
  ISearchResults,
  // First argument to the payload creator
  Partial<ISearchData>,
  // Types for ThunkAPI
  { rejectValue: IErrorPayload }
>("search/search", async (data, { rejectWithValue }) => {
  try {
    return await SearchService.search({
      page: data.page || 1,
      query: data.query || "",
      amount: NB_SEARCH_RESULT_ITEMS,
      type: data.type || undefined,
      prioritiseDossiersAndTools: false,
    });
  } catch (error) {
    return rejectWithValue(error as IErrorPayload);
  }
});

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    clearSearchState: (state) => {
      state.data = [];
      state.meta = initialState.meta;
    },
  },
  extraReducers: (builder) => {
    // search pending
    builder.addCase(doSearch.pending, (state, { meta }) => {
      state.meta = {
        ...state.meta,
        status: IAPIState.pending,
        error: null,
        query: meta.arg.query, // update current query in state
        pagination: {
          ...state.meta.pagination, // keep current pagination, update page
          page: meta.arg.page || 1,
        },
      };
    });
    // search failed
    builder.addCase(doSearch.rejected, (state, { payload }) => {
      state.meta = {
        status: IAPIState.failed,
        error: payload?.error,
        pagination: undefined, // remove pagination on error
      };
    });
    // search succeeded
    builder.addCase(doSearch.fulfilled, (state, { payload, meta }) => {
      state.meta = {
        ...state.meta,
        status: IAPIState.succeeded,
        error: null,
        pagination: {
          page: meta.arg.page || 1, // if no page provided, fallback to  page 1
          amount: NB_SEARCH_RESULT_ITEMS,
          total: payload.total,
        },
        hasSearched: true,
      };

      state.data = payload.results;
    });
  },
});

export const searchMetaSelector = createSelector(
  (state: IStoreState) => state.search,
  (search) => search.meta
);

export const searchDataSelector = createSelector(
  (state: IStoreState) => state.search,
  (search) => search.data
);

export const { clearSearchState } = searchSlice.actions;
export const searchReducer = searchSlice.reducer;
